import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { EstimatesTableView } from '@/components/estimates/EstimatesTableView';
import { EstimateSummaryCards } from '@/components/estimates/EstimateSummaryCards';
import { EstimateQuickViewSheet } from '@/components/estimates/EstimateQuickViewSheet';
import { useEstimateStats, useExpiringSoonEstimates, useEstimates } from '@/hooks/useEstimates';
import { useQuickViewNavigation } from '@/hooks/useQuickViewNavigation';
import { Clock, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NewEstimateDialog } from '@/components/estimates/NewEstimateDialog';
import { AdminOnly } from '@/components/AdminOnly';

export default function Estimates() {
  const { data: stats, isLoading: statsLoading } = useEstimateStats();
  const { data: expiringSoon } = useExpiringSoonEstimates();
  const { data: estimates = [] } = useEstimates();

  // Quick view state (/estimates/:id opens it directly — shareable deep link)
  const { id: routeEstimateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(routeEstimateId ?? null);

  useEffect(() => {
    if (routeEstimateId) setSelectedEstimateId(routeEstimateId);
  }, [routeEstimateId]);

  const closeQuickView = () => {
    setSelectedEstimateId(null);
    if (routeEstimateId) navigate('/estimates', { replace: true });
  };

  // "View expiring" banner filter
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [showNewEstimate, setShowNewEstimate] = useState(false);

  // Quick view navigation
  const { hasPrevious, hasNext, goToPrevious, goToNext } = useQuickViewNavigation({
    items: estimates,
    getItemId: (estimate) => estimate.id,
    selectedId: selectedEstimateId,
    onSelect: setSelectedEstimateId,
  });

  return (
    <Layout>
      <div className="space-y-[22px]">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Estimates</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Create and manage estimates for potential customers
            </p>
          </div>
          <Button onClick={() => setShowNewEstimate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Estimate
          </Button>
        </div>

        <NewEstimateDialog open={showNewEstimate} onOpenChange={setShowNewEstimate} />

        {/* Expiring Soon Banner */}
        {expiringSoon && expiringSoon.length > 0 && (
          <div className="flex items-center gap-3 rounded-lg bg-warning/10 px-4 py-3 text-warning">
            <Clock className="h-4 w-4 shrink-0" />
            <p className="text-[13px] font-medium">
              {expiringSoon.length} estimate{expiringSoon.length > 1 ? 's' : ''} will expire within the next 3 days.
              Follow up with customers to secure the work.
            </p>
            <button
              type="button"
              onClick={() => setExpiringOnly(true)}
              className="ml-auto shrink-0 text-[13px] font-semibold underline-offset-4 hover:underline"
            >
              View expiring
            </button>
          </div>
        )}

        {/* Summary Cards */}
        <AdminOnly>
          <EstimateSummaryCards stats={stats} isLoading={statsLoading} />
        </AdminOnly>

        {/* Table */}
        <EstimatesTableView
          onEstimateClick={setSelectedEstimateId}
          expiringOnly={expiringOnly}
          onExpiringOnlyChange={setExpiringOnly}
        />

        {/* Estimate Quick View Sheet */}
        <EstimateQuickViewSheet
          estimateId={selectedEstimateId}
          open={!!selectedEstimateId}
          onOpenChange={(open) => !open && closeQuickView()}
          onPrevious={hasPrevious ? goToPrevious : undefined}
          onNext={hasNext ? goToNext : undefined}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      </div>
    </Layout>
  );
}
